import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ServiceCard from '../components/ServiceCard';


export default function Home() {
  const { t } = useTranslation('marketing');

  const services = [
    {
      id: 'longevity',
      title: t('home.services.longevity.title'),
      description: t('home.services.longevity.description'),
      icon: 'favorite',
      image: '/images/service-longevity.jpeg'
    },
    {
      id: 'wellness',
      title: t('home.services.wellness.title'),
      description: t('home.services.wellness.description'),
      icon: 'health_and_safety',
      image: '/images/service-wellness.jpeg'
    },
    {
      id: 'personalized',
      title: t('home.services.personalized.title'),
      description: t('home.services.personalized.description'),
      icon: 'assignment',
      image: '/images/service-personalized.jpg'
    },
    {
      id: 'monitoring',
      title: t('home.services.monitoring.title'),
      description: t('home.services.monitoring.description'),
      icon: 'trending_up',
      image: '/images/service-monitoring.jpg'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="hero-gradient min-h-screen flex flex-col justify-center items-center px-margin-mobile py-20 relative overflow-hidden">
        <div className="ambient-shadow" />

        <div className="max-w-4xl mx-auto text-center z-10">
          <h1 className="text-display-lg text-on-primary mb-6 leading-tight">
            {t('home.hero.title')}
          </h1>

          <p className="text-headline-md text-on-primary/90 mb-12 max-w-2xl mx-auto">
            {t('home.hero.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-accent text-on-accent rounded-full font-bold text-label-lg uppercase tracking-widest hover:opacity-90 transition-opacity"
            >
              {t('home.hero.cta1')}
            </Link>

            <Link
              to="/services"
              className="px-8 py-4 bg-primary-container text-on-primary-container rounded-full font-bold text-label-lg uppercase tracking-widest border border-outline-variant hover:bg-primary-container/80 transition-colors"
            >
              {t('home.hero.cta2')}
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <span className="material-symbols-outlined text-3xl text-on-primary opacity-60">expand_more</span>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-section-gap px-margin-mobile bg-background">
        <div className="max-w-container-max mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-display-md text-on-background mb-4">{t('home.services.heading')}</h2>
            <p className="text-headline-md text-on-background/70 max-w-xl mx-auto">
              {t('home.services.subheading')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <ServiceCard
                key={service.id}
                {...service}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Premium Products Section */}
      <section className="py-section-gap px-margin-mobile bg-primary-container">
        <div className="max-w-container-max mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-display-md text-on-primary-container mb-4">{t('home.products.heading')}</h2>
            <p className="text-headline-md text-on-primary-container/70 max-w-xl mx-auto">
              {t('home.products.subheading')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Link to="/products" className="bg-background rounded-2xl overflow-hidden border border-outline-variant/30 hover:shadow-lg transition-shadow">
              <div className="aspect-square bg-gradient-to-br from-secondary to-primary overflow-hidden">
                <img src="/images/PureelyDetox.jpeg" alt={t('home.products.pureelyDetox.name')} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <h3 className="text-headline-md font-bold mb-2 text-on-background">{t('home.products.pureelyDetox.name')}</h3>
                <p className="text-body-md text-on-background/70 mb-4">{t('home.products.pureelyDetox.description')}</p>
                <p className="text-accent font-bold">{t('home.products.pureelyDetox.price')}</p>
              </div>
            </Link>

            <Link to="/products" className="bg-background rounded-2xl overflow-hidden border border-outline-variant/30 hover:shadow-lg transition-shadow">
              <div className="aspect-square bg-gradient-to-br from-secondary to-primary overflow-hidden">
                <img src="/images/ReYouth.jpeg" alt={t('home.products.reYouth.name')} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <h3 className="text-headline-md font-bold mb-2 text-on-background">{t('home.products.reYouth.name')}</h3>
                <p className="text-body-md text-on-background/70 mb-4">{t('home.products.reYouth.description')}</p>
                <p className="text-accent font-bold">{t('home.products.reYouth.price')}</p>
              </div>
            </Link>

            <Link to="/products" className="bg-background rounded-2xl overflow-hidden border border-outline-variant/30 hover:shadow-lg transition-shadow">
              <div className="aspect-square bg-gradient-to-br from-secondary to-primary overflow-hidden">
                <img src="/images/TrueLife.jpeg" alt={t('home.products.trueLife.name')} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <h3 className="text-headline-md font-bold mb-2 text-on-background">{t('home.products.trueLife.name')}</h3>
                <p className="text-body-md text-on-background/70 mb-4">{t('home.products.trueLife.description')}</p>
                <p className="text-accent font-bold">{t('home.products.trueLife.price')}</p>
              </div>
            </Link>
          </div>

          <div className="text-center">
            <Link
              to="/products"
              className="inline-block px-12 py-4 bg-accent text-on-accent rounded-full font-bold text-label-lg uppercase tracking-widest hover:opacity-90 transition-opacity"
            >
              {t('home.products.shopAll')}
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-on-primary py-section-gap px-margin-mobile">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-display-md mb-6">{t('home.cta.title')}</h2>
          <p className="text-headline-md mb-12 opacity-90">
            {t('home.cta.description')}
          </p>

          <Link
            to="/contact"
            className="inline-block px-12 py-4 bg-accent text-on-accent rounded-full font-bold text-label-lg uppercase tracking-widest hover:opacity-90 transition-opacity"
          >
            {t('home.cta.button')}
          </Link>
        </div>
      </section>
    </div>
  );
}
